import { runMoveSkillToTrashWorkflow } from "@/action-dialog";
import { undoRestoreSkillErrorMessage, useRestoreSkill } from "@/api/archived";
import { useOrgIntegrations } from "@/api/integrations";
import {
  useDeleteSkill,
  usePublishSkill,
  usePutSkill,
  useSkillById,
  useSkillFiles,
} from "@/api/skills";
import { PaneBackButton } from "@/components/PaneBackButton";
import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import { DropZoneProvider } from "@/dnd/OsFileDropLayer";
import { useAppNavigate, usePaneParams } from "@/hooks/pane-routing";
import {
  ObjectActionPaneHeaderKebabMenu,
  useObjectActions,
  type ObjectActionHandlers,
  type ObjectKind,
} from "@/object-actions";
import { SkillDetailContent } from "@/pages/skills/SkillDetailContent";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { SkillContentEditor } from "@/panes/skill/SkillContentEditor";
import { cn } from "@/utils";
import { downloadSkillFilesAsZip } from "@/utils/downloadSkillFilesAsZip";
import { route } from "@/utils/route";
import type { MetadataIcon } from "@cohesive/api-client";
import {
  Icon,
  IconSelector,
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
  Spinner,
} from "@cohesive/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { ScrollText, X } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const SKILL_KIND: ObjectKind = { type: "skill" };

export const SkillPane = () => {
  const params = usePaneParams(ROUTES.SKILLS.SHOW);
  const skillId = params.id;
  const navigate = useNavigate();
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();
  const queryClient = useQueryClient();

  const mode = skillId === "new" ? "create" : "edit";
  const isCreateMode = mode === "create";

  const [isDownloading, setIsDownloading] = useState(false);

  const {
    data: skill,
    isLoading: isSkillLoading,
    isError: isSkillError,
  } = useSkillById(skillId || "", {
    enabled: !isCreateMode && !!skillId,
  } as any);

  const { data: files = [], isLoading: isFilesLoading } = useSkillFiles(
    skillId || "",
    {
      enabled: !isCreateMode && !!skillId,
    } as any,
  );

  const deleteSkillMutation = useDeleteSkill();
  const restoreSkillMutation = useRestoreSkill();
  const { mutate: publishSkill, isPending: isPublishing } = usePublishSkill({
    onSuccess: () => toast.success("Published to your organization"),
    onError: (error) => toast.error(error.message || "Failed to publish skill"),
  });

  const optimisticStatus = useRef<string | null>(null);

  const { mutate: updateSkill, isPending: isTogglingStatus } = usePutSkill({
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      queryClient.invalidateQueries({ queryKey: ["skill", skillId] });
      toast.success(
        `Skill set to ${data.status === "enabled" ? "Auto" : "Manual"}`,
      );
    },
    onError: (error) => {
      optimisticStatus.current = null;
      toast.error(error.message || "Failed to update skill");
    },
  });

  // The icon edits in place from the header; description + files live in the
  // content editor below. Icon save is quiet (usePutSkill invalidates the cache).
  const { mutateAsync: updateSkillMeta } = usePutSkill({
    onError: (error) => toast.error(error.message || "Failed to update skill"),
  });
  const { data: integrations = [] } = useOrgIntegrations();

  const handleIconChange = (icon: MetadataIcon) => {
    if (!skill) return;
    updateSkillMeta({ id: skill.id, data: { icon } }).catch(() => {});
  };

  const moveSkillToTrash = useCallback(() => {
    if (!skill) return;
    void runMoveSkillToTrashWorkflow({
      skill,
      deleteSkill: deleteSkillMutation,
      restoreSkill: restoreSkillMutation,
      onRestoreError: (err) => toast.error(undoRestoreSkillErrorMessage(err)),
      onSuccess: () => {
        window.setTimeout(() => {
          if (paneContext) {
            appNavigate({ pane: "rightPane", to: null });
          } else {
            navigate(route(ROUTES.SKILLS.ROOT));
          }
        }, 200);
      },
    });
  }, [
    skill,
    deleteSkillMutation,
    restoreSkillMutation,
    paneContext,
    appNavigate,
    navigate,
  ]);

  const handleToggleStatus = () => {
    if (!skill || isCreateMode) return;
    const newStatus = skill.status === "enabled" ? "disabled" : "enabled";
    optimisticStatus.current = newStatus;
    updateSkill({ id: skill.id, data: { status: newStatus } });
  };

  const handleDownloadZip = useCallback(async () => {
    if (!skill || files.length === 0) return;

    setIsDownloading(true);
    try {
      await downloadSkillFilesAsZip(files, skill.name);
      toast.success("Skill downloaded");
    } catch (e) {
      console.error(e);
      toast.error(
        e instanceof Error ? e.message : "Failed to download skill files",
      );
    } finally {
      setIsDownloading(false);
    }
  }, [skill, files]);

  const displayEntity = isCreateMode ? undefined : skill;

  if (
    optimisticStatus.current &&
    displayEntity?.status === optimisticStatus.current
  ) {
    optimisticStatus.current = null;
  }

  const effectiveStatus = optimisticStatus.current ?? displayEntity?.status;
  const isEnabled = effectiveStatus === "enabled";

  const canDownload = !isCreateMode && files.length > 0;

  const [kebabOpen, setKebabOpen] = useState(false);

  // The kebab carries only Publish / Download / Move to Trash. Auto/Manual and
  // edit are served by the bespoke status pill and the editor itself, so we
  // omit their handlers — the resolver drops any action without one, so they
  // never surface here. Publish/download guard against re-entry since registry
  // menu items no longer disable per pending mutation.
  const kebabHandlers = useMemo((): ObjectActionHandlers => {
    if (isCreateMode || !skillId) return {};
    return {
      publish: () =>
        window.setTimeout(() => {
          if (isPublishing) return;
          publishSkill(skillId);
        }, 0),
      download: canDownload
        ? () =>
            window.setTimeout(() => {
              if (isDownloading) return;
              void handleDownloadZip();
            }, 0)
        : undefined,
      delete: () => window.setTimeout(() => moveSkillToTrash(), 0),
    };
  }, [
    isCreateMode,
    skillId,
    isPublishing,
    publishSkill,
    canDownload,
    isDownloading,
    handleDownloadZip,
    moveSkillToTrash,
  ]);

  const { actions: kebabActions } = useObjectActions({
    kind: SKILL_KIND,
    surface: "left-rail",
    enabled: kebabOpen && !isCreateMode,
    handlers: kebabHandlers,
  });

  return (
    <>
      <DropZoneProvider>
        <PaneLayout>
          <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
            <PaneLayoutHeaderActions
              className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
            >
              <PaneBackButton />
              {!isCreateMode && displayEntity && (
                <div className="flex min-w-0 items-center gap-2 pl-1">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        aria-label="Change skill icon"
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border text-n-50 transition-colors hover:bg-n-05"
                      >
                        {displayEntity.icon?.type ? (
                          <Icon icon={displayEntity.icon} size={16} />
                        ) : (
                          <ScrollText className="h-4 w-4" />
                        )}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-[352px] p-0">
                      <IconSelector
                        onIconChange={handleIconChange}
                        selectedIcon={
                          displayEntity.icon?.type
                            ? displayEntity.icon
                            : undefined
                        }
                        integrations={integrations}
                      />
                    </PopoverContent>
                  </Popover>
                  <span className="truncate text-sm font-medium">
                    {displayEntity.name}
                  </span>
                </div>
              )}
              <div className="flex-1" />
              <TooltipProvider>
                {!isCreateMode && displayEntity && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={handleToggleStatus}
                        disabled={isTogglingStatus}
                        className="flex h-7 items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors hover:bg-n-05 disabled:opacity-60"
                      >
                        <span
                          className={cn(
                            "size-1.5 rounded-full",
                            isEnabled ? "bg-lc-active" : "bg-n-40",
                          )}
                          aria-hidden
                        />
                        {isEnabled ? "Auto" : "Manual"}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isEnabled
                        ? "Auto — in the agent's prompt and mounted. Click for Manual."
                        : "Manual — mention it in the picker. Click for Auto."}
                    </TooltipContent>
                  </Tooltip>
                )}
                {!isCreateMode && (
                  // Registry-driven kebab (surface `left-rail`).
                  // ObjectActionPaneHeaderKebabMenu is `modal={false}` under the
                  // hood: the delete action opens the archive AlertDialog, and a
                  // modal dropdown + dialog both lock <body> pointer-events,
                  // which Radix miscounts across close/unmount — freezing the
                  // app. A non-modal menu avoids the nested lock.
                  <ObjectActionPaneHeaderKebabMenu
                    actions={kebabActions}
                    kind={SKILL_KIND}
                    name={displayEntity?.name}
                    open={kebabOpen}
                    onOpenChange={setKebabOpen}
                  />
                )}
                {paneContext && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="toolbarGhost"
                        size="icon-sm"
                        className="shrink-0"
                        onClick={() =>
                          appNavigate({ pane: "rightPane", to: null })
                        }
                        aria-label="Close"
                      >
                        <X />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Close</TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>
            </PaneLayoutHeaderActions>
          </PaneLayoutHeader>
          <PaneLayoutContent>
            {isCreateMode ? (
              <div className="h-full overflow-y-auto p-4">
                <SkillDetailContent mode="create" context="pane" />
              </div>
            ) : isSkillLoading ? (
              <div className="flex h-full items-center justify-center">
                <Spinner size="lg" />
              </div>
            ) : isSkillError || !skill ? (
              // Distinguish a failed/removed skill from loading so the pane never
              // spins forever (e.g. the skill was deleted in another tab).
              <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
                <p className="text-sm font-medium text-foreground/80">
                  This skill couldn’t be loaded
                </p>
                <p className="text-sm text-muted-foreground">
                  It may have been deleted or you no longer have access.
                </p>
              </div>
            ) : (
              <div className="flex h-full flex-col overflow-hidden p-4">
                <SkillContentEditor
                  key={skill.id}
                  skill={skill}
                  files={files}
                  filesLoading={isFilesLoading}
                />
              </div>
            )}
          </PaneLayoutContent>
        </PaneLayout>
      </DropZoneProvider>
    </>
  );
};
